import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useFetchAthleteByIdQuery, useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import { useGetGarminAuthUrlMutation, useGetGarminStatusQuery, useDisconnectGarminMutation, useLinkGarminFromRecentPushMutation } from '../slices/garminApiSlice';
import { useGetImportStatusQuery, useGetStravaAuthUrlMutation, useImportStravaActivitiesMutation } from '../slices/stravaApiSlice';
import { useGetTeamLeaderboardQuery } from '../slices/teamApiSlice';
import { useGetCurrentSeasonQuery } from '../slices/seasonsApiSlice';
import { useFetchLeaguesQuery } from '../slices/leaguesApiSlice';
import { useLazyRefreshUserProfileQuery } from '../slices/usersApiSlice';
import { useDeleteEventMutation } from '../slices/eventApiSlice';
import { setCredentials } from '../slices/authSlice';
import Icon from '../components/Icon';
import { Activity, Flag, Trophy, Users, Wallet } from 'lucide-react';
import './AthleteScreen.css';

const getEntityName = (entity, fallback = 'Not set') => {
  if (!entity) return fallback;
  if (typeof entity === 'string') return entity;
  return entity.name || entity.title || fallback;
};

const getAthleteFullName = (athlete, fallback = 'Unknown athlete') => {
  if (!athlete) return fallback;
  const fullName = [athlete.firstName, athlete.lastName].filter(Boolean).join(' ').trim();
  return fullName || fallback;
};

const getResolvedDraftedTeam = (athlete) => {
  if (!athlete) return null;

  if (athlete.lastDraftedTeam) return athlete.lastDraftedTeam;
  if (athlete.draftedTeam) return athlete.draftedTeam;
  if (Array.isArray(athlete.draftedTeams) && athlete.draftedTeams.length > 0) {
    return athlete.draftedTeams[athlete.draftedTeams.length - 1];
  }

  return null;
};

const getCurrentDraftedTeams = (athlete) => {
  if (!athlete) return [];

  const candidates = [
    ...(Array.isArray(athlete.draftedTeams) ? athlete.draftedTeams : []),
    athlete.lastDraftedTeam,
    athlete.draftedTeam,
  ].filter(Boolean);

  const seen = new Set();
  return candidates.filter((team) => {
    const teamId = team?._id || team;
    if (!teamId) return false;

    const normalizedId = String(teamId);
    if (seen.has(normalizedId)) return false;

    seen.add(normalizedId);
    return true;
  });
};

const formatActivityDate = (dateValue) => {
  if (!dateValue) return 'Unknown date';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Unknown date';

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const roundToSingleDecimal = (value) => Math.round(value * 10) / 10;

const formatMiles = (distanceMeters) => {
  if (typeof distanceMeters !== 'number') return '0.0';
  return (distanceMeters / 1609.34).toFixed(1);
};

const getActivityService = (event) => {
  if (event?.importedFromGarmin) return 'Garmin';
  if (event?.importedFromStrava) return 'Strava';
  const labels = {
    manual: 'Manual',
    strava: 'Strava',
    garmin: 'Garmin',
    apple_health: 'Apple Health',
    apple_watch: 'Apple Watch',
    iphone: 'iPhone',
  };
  if (event?.source && labels[event.source]) return labels[event.source];
  return 'Manual';
};

const getActivityDevice = (event) => {
  const garminLabel = [event?.garminManufacturer, event?.garminProduct]
    .filter(Boolean)
    .join(' ')
    .trim();

  return event?.garminDeviceName || garminLabel || 'Not provided';
};

const AthleteScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [deletingEventId, setDeletingEventId] = useState(null);
  const athleteId = searchParams.get('id');
  const stravaConnected = searchParams.get('strava_connected');
  const garminConnected = searchParams.get('garmin_connected');
  const { userInfo } = useSelector((state) => state.auth);

  const { data: athlete, error: athleteError, isLoading: athleteLoading } = useFetchAthleteByUserIdQuery(
    userInfo?._id,
    { skip: !userInfo?._id }
  );
  const { data: specificAthlete, error: specificAthleteError, isLoading: specificAthleteLoading } = useFetchAthleteByIdQuery(
    athleteId,
    { skip: !athleteId }
  );
  const displayedAthlete = specificAthlete || athlete;
  const isOwnProfile = !athleteId || athleteId === athlete?._id;
  const leagueId = displayedAthlete?.league?._id || displayedAthlete?.league;
  const effectiveAthleteId = userInfo?.athleteId || athlete?._id || displayedAthlete?._id;
  const resolvedDraftedTeam = getResolvedDraftedTeam(displayedAthlete);
  const currentDraftedTeams = getCurrentDraftedTeams(displayedAthlete);
  const draftedTeamId = resolvedDraftedTeam?._id || resolvedDraftedTeam;

  const { data: garminStatus } = useGetGarminStatusQuery(undefined, { skip: !userInfo || !isOwnProfile });
  const [getGarminAuthUrl] = useGetGarminAuthUrlMutation();
  const [refreshUserProfile] = useLazyRefreshUserProfileQuery();
  const { data: importStatus, refetch: refetchImportStatus } = useGetImportStatusQuery(undefined, {
    skip: !userInfo || !isOwnProfile,
  });
  const { data: userLeague } = useFetchLeaguesQuery(leagueId, { skip: !leagueId });
  const { data: leaderboard } = useGetTeamLeaderboardQuery(leagueId, { skip: !leagueId });
  const { data: currentSeasonResponse } = useGetCurrentSeasonQuery(leagueId, { skip: !leagueId });
  const [getStravaAuthUrl] = useGetStravaAuthUrlMutation();
  const [importActivities, { isLoading: importLoading }] = useImportStravaActivitiesMutation();
  const [deleteEvent] = useDeleteEventMutation();
  const [disconnectGarmin, { isLoading: disconnectingGarmin }] = useDisconnectGarminMutation();
  const [linkGarminFromRecentPush, { isLoading: linkingGarminId }] = useLinkGarminFromRecentPushMutation();

  useEffect(() => {
    if (stravaConnected === 'true' && userInfo) {
      const clearStravaConnectedParam = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('strava_connected');
        const nextUrl = nextParams.toString() ? `${window.location.pathname}?${nextParams.toString()}` : window.location.pathname;
        window.history.replaceState({}, '', nextUrl);
      };

      const refreshProfile = async () => {
        try {
          const result = await refreshUserProfile().unwrap();
          dispatch(setCredentials({
            userInfo: {
              ...userInfo,
              ...result,
            },
          }));
          toast.success('Strava connected successfully.');
          clearStravaConnectedParam();
          refetchImportStatus?.();
        } catch (error) {
          console.error('Failed to refresh after Strava connection', error);
          clearStravaConnectedParam();
          toast.error('Strava connected, but the profile did not refresh cleanly.');
        }
      };

      refreshProfile();
    }
  }, [dispatch, refetchImportStatus, refreshUserProfile, searchParams, stravaConnected, userInfo]);

  useEffect(() => {
    if (garminConnected === 'true') {
      toast.success('Garmin connected successfully.');
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('garmin_connected');
      const nextUrl = nextParams.toString() ? `${window.location.pathname}?${nextParams.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [garminConnected, searchParams]);

  const handleConnectStrava = async () => {
    try {
      const { authUrl } = await getStravaAuthUrl().unwrap();
      window.location.assign(authUrl);
    } catch (error) {
      console.error('Failed to start Strava auth', error);
      toast.error(error?.data?.message || 'Failed to start Strava connection');
    }
  };

  const handleConnectGarmin = async () => {
    try {
      const { authUrl } = await getGarminAuthUrl().unwrap();
      window.open(
        authUrl,
        'garmin-auth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );
    } catch (error) {
      console.error('Failed to start Garmin auth', error);
      toast.error(error?.data?.message || 'Failed to start Garmin connection');
    }
  };

  const handleImportActivities = async () => {
    try {
      const result = await importActivities().unwrap();

      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} activities.`);
      } else if (result.message) {
        toast.info(result.message);
      } else {
        toast.info('No new eligible Strava activities were available to import.');
      }

      refetchImportStatus?.();
    } catch (error) {
      console.error('Import failed', error);
      toast.error(error?.data?.message || 'Failed to import activities');
    }
  };

  const handleDisconnectGarmin = async () => {
    try {
      await disconnectGarmin({ notify: true }).unwrap();
      toast.success('Garmin disconnected.');
    } catch (error) {
      console.error('Garmin disconnect failed', error);
      toast.error('Failed to disconnect Garmin');
    }
  };

  const handleDeleteActivity = async (eventId) => {
    if (!eventId || !isOwnProfile) {
      return;
    }

    if (!window.confirm('Delete this activity from your athlete stats?')) {
      return;
    }

    setDeletingEventId(eventId);

    try {
      await deleteEvent(eventId).unwrap();
      toast.success('Activity deleted.');
      refetchImportStatus?.();
    } catch (error) {
      console.error('Activity delete failed', error);
      toast.error(error?.data?.message || 'Failed to delete activity');
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleLinkGarminId = async () => {
    try {
      const result = await linkGarminFromRecentPush().unwrap();
      if (result?.linked) {
        toast.success(`Garmin ID linked${result.backfilled ? ` and ${result.backfilled} activities backfilled` : ''}.`);
        return;
      }
      if (result?.reason === 'multiple-candidates') {
        toast.error('Multiple Garmin IDs were detected in recent syncs, so LeagueRunner will not guess. Please contact support.');
        return;
      }
      toast.info(result?.message || 'Garmin ID is still waiting on a recent sync from Garmin.');
    } catch (error) {
      console.error('Garmin manual link failed', error);
      toast.error(error?.data?.message || 'Failed to link Garmin ID');
    }
  };

  if (athleteLoading || specificAthleteLoading) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-secondary">Loading athlete profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (athleteError || specificAthleteError) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <Alert variant="danger">
            <Alert.Heading>Error Loading Athlete Profile</Alert.Heading>
            <p>Please try again or return to your profile.</p>
            <Button variant="outline-danger" onClick={() => navigate('/profile')}>
              Go to Profile
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  if (!displayedAthlete) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="text-center py-5">
            <h2 className="text-secondary mb-3">No Athlete Profile Found</h2>
            <p className="text-secondary mb-4">Create a profile to join leagues, get drafted, and score points for your team.</p>
            <Button variant="primary" onClick={() => navigate('/profile')}>
              Create Profile
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const draftedTeamName = getEntityName(resolvedDraftedTeam, 'No drafted team');
  const draftedTeamNames = currentDraftedTeams.map((team) => getEntityName(team)).join(', ');
  const ownedTeamName = getEntityName(displayedAthlete.team, 'No team yet');
  const leagueName = getEntityName(displayedAthlete.league, 'No league');
  const isLeagueAdmin =
    leagueId &&
    String(userLeague?.admin?._id || userLeague?.admin) === String(effectiveAthleteId);
  const displayedAthleteName = getAthleteFullName(displayedAthlete);
  const pageTitle = isOwnProfile ? 'My Dashboard' : displayedAthleteName;
  const pageSubtitle = isOwnProfile
    ? 'Your season snapshot, team context, and synced activity in one place.'
    : 'Season snapshot, team context, and counted activity.';
  const salary = displayedAthlete.salary || 0;
  const leaderboardRows = leaderboard?.leaderboard || [];
  const draftedTeamStanding = leaderboardRows.find((team) => team.teamId === draftedTeamId);
  const currentSeason = currentSeasonResponse?.season;
  const currentSeasonStart = currentSeason?.startDate ? new Date(currentSeason.startDate) : null;
  const currentSeasonEnd = currentSeason?.endDate ? new Date(currentSeason.endDate) : null;
  const hasSeasonWindow = Boolean(
    currentSeasonStart &&
    currentSeasonEnd &&
    !Number.isNaN(currentSeasonStart.getTime()) &&
    !Number.isNaN(currentSeasonEnd.getTime())
  );
  const seasonActivities = hasSeasonWindow
    ? [...(displayedAthlete.events || [])]
        .filter((event) => {
          if (!event?.startTime) return false;
          const eventDate = new Date(event.startTime);
          if (Number.isNaN(eventDate.getTime())) return false;
          return eventDate >= currentSeasonStart && eventDate <= currentSeasonEnd;
        })
        .sort((left, right) => new Date(right.startTime).getTime() - new Date(left.startTime).getTime())
    : [];
  const totalPoints = displayedAthlete.seasonTotalPoints ?? roundToSingleDecimal(
    seasonActivities.reduce((sum, event) => sum + (event.points || 0), 0)
  );
  const totalMiles = displayedAthlete.seasonTotalMiles ?? roundToSingleDecimal(
    seasonActivities.reduce((sum, event) => sum + ((event.distance || 0) / 1609.34), 0)
  );
  const totalActivities = displayedAthlete.seasonTotalActivities ?? seasonActivities.length;
  const thisWeekPoints = displayedAthlete.currentWeekPoints ?? 0;
  const thisWeekRangeLabel = displayedAthlete.currentWeekStart && displayedAthlete.currentWeekEnd
    ? `${formatActivityDate(displayedAthlete.currentWeekStart)} - ${formatActivityDate(displayedAthlete.currentWeekEnd)}`
    : 'Sunday through Saturday';

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-5">
            <div>
              <h1 className="text-primary mb-2">{pageTitle}</h1>
              <p className="text-secondary mb-2">{pageSubtitle}</p>
              <div className="d-flex gap-2 flex-wrap">
                <Badge bg={currentDraftedTeams.length > 0 ? 'success' : 'secondary'}>
                  {currentDraftedTeams.length > 0 ? `On teams: ${draftedTeamNames}` : 'No drafted team'}
                </Badge>
              </div>
            </div>

            {isOwnProfile && (
              <div className="d-flex gap-2 flex-wrap">
                <Button variant="outline-primary" onClick={() => navigate('/leagues')}>
                  View League
                </Button>
                <Button variant="outline-primary" onClick={() => navigate('/team')}>
                  View Team
                </Button>
                <Button variant="outline-primary" onClick={() => navigate('/profile')}>
                  Edit Profile
                </Button>
              </div>
            )}
          </div>

          <Row className="mb-4 g-3">
            <Col md={3}>
              <Card className="modern-card h-100">
                <Card.Body className="text-center">
                  <Icon icon={Trophy} size={22} className="mb-2" />
                  <h5 className="card-title">Season Points</h5>
                  <h3 className="text-primary">{Number(totalPoints).toFixed(1)}</h3>
                  <p className="text-secondary mb-0">Fantasy points in the current season</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="modern-card h-100">
                <Card.Body className="text-center">
                  <Icon icon={Activity} size={22} className="mb-2" />
                  <h5 className="card-title">This Week</h5>
                  <h3 className="text-primary">{Number(thisWeekPoints).toFixed(1)}</h3>
                  <p className="text-secondary mb-0">{thisWeekRangeLabel}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="modern-card h-100">
                <Card.Body className="text-center">
                  <Icon icon={Users} size={22} className="mb-2" />
                  <h5 className="card-title">Roster Status</h5>
                  <h3 className="text-primary">{currentDraftedTeams.length > 0 ? 'On teams' : 'No drafted team'}</h3>
                  <p className="text-secondary mb-0">{currentDraftedTeams.length > 0 ? draftedTeamNames : 'No drafted team'}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="modern-card h-100">
                <Card.Body className="text-center">
                  <Icon icon={Wallet} size={22} className="mb-2" />
                  <h5 className="card-title">Salary</h5>
                  <h3 className="text-primary">${salary.toLocaleString()}</h3>
                  <p className="text-secondary mb-0">Current roster value</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={7}>
              <Card className="modern-card mb-4">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">Competition Snapshot</h3>
                  <p className="modern-card-subtitle">The pieces that matter for league play.</p>
                </div>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table mb-0">
                      <tbody>
                        <tr>
                          <th>League</th>
                          <td>{leagueName}</td>
                        </tr>
                        <tr>
                          <th>Your Team</th>
                          <td>{ownedTeamName}</td>
                        </tr>
                        <tr>
                          <th>Drafted Team</th>
                          <td>{draftedTeamName}</td>
                        </tr>
                        <tr>
                          <th>Drafted Team Rank</th>
                          <td>{draftedTeamStanding ? `#${draftedTeamStanding.rank}` : 'Not available yet'}</td>
                        </tr>
                        <tr>
                          <th>Season Miles</th>
                          <td>{Number(totalMiles).toFixed(1)} mi</td>
                        </tr>
                        <tr>
                          <th>Season Activities Counted</th>
                          <td>{totalActivities}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>

              <Card className="modern-card">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">Current Season Activities</h3>
                  <p className="modern-card-subtitle">Runs counted for this season, newest first.</p>
                </div>
                <Card.Body>
                  {!hasSeasonWindow ? (
                    <p className="mb-0 text-secondary">No active season window is available for this league yet.</p>
                  ) : seasonActivities.length === 0 ? (
                    <p className="mb-0 text-secondary">No runs have been recorded for this athlete in the current season.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Miles</th>
                            <th>Service</th>
                            <th>Device</th>
                            {isOwnProfile && <th className="text-end">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {seasonActivities.map((event) => (
                            <tr key={event._id || `${event.startTime}-${event.distance}`}>
                              <td>{formatActivityDate(event.startTime)}</td>
                              <td>{formatMiles(event.distance)} mi</td>
                              <td>{getActivityService(event)}</td>
                              <td>{getActivityDevice(event)}</td>
                              {isOwnProfile && (
                                <td className="text-end">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteActivity(event._id)}
                                    disabled={!event._id || deletingEventId === event._id}
                                  >
                                    {deletingEventId === event._id ? 'Deleting...' : 'Delete'}
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="modern-card mb-4">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">League Actions</h3>
                  <p className="modern-card-subtitle">Jump straight to the places where competition happens.</p>
                </div>
                <Card.Body className="d-grid gap-2">
                  <Button variant="primary" onClick={() => navigate('/leagues')}>
                    Open League Standings
                  </Button>
                  {isLeagueAdmin && (
                    <Button variant="outline-primary" onClick={() => navigate(`/leagues/${leagueId}/manage`)}>
                      Manage League
                    </Button>
                  )}
                  <Button variant="outline-primary" onClick={() => navigate(displayedAthlete.team?._id ? `/team/${displayedAthlete.team._id}` : '/team')}>
                    Open Team Page
                  </Button>
                  {resolvedDraftedTeam?._id && (
                    <Button variant="outline-primary" onClick={() => navigate(`/team/${resolvedDraftedTeam._id}`)}>
                      View Drafted Team
                    </Button>
                  )}
                </Card.Body>
              </Card>

              {isOwnProfile && (
                <Card className="modern-card">
                  <div className="modern-card-header">
                    <h3 className="modern-card-title">Sync Status</h3>
                    <p className="modern-card-subtitle">Keep your fantasy points current.</p>
                  </div>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span className="text-secondary">Strava</span>
                        <strong>{userInfo?.hasStravaConnection ? 'Connected' : 'Not connected'}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-secondary">Garmin</span>
                        <strong>{garminStatus?.awaitingLink ? 'Connected, ID link required' : (garminStatus?.connected ? 'Connected' : 'Not connected')}</strong>
                      </div>
                    </div>
                    {garminStatus?.manualLinkRequired && (
                      <Alert variant="warning" className="py-2">
                        Garmin is connected, but imports are paused until you explicitly link the Garmin ID from a recent sync.
                      </Alert>
                    )}

                    <div className="d-grid gap-2">
                      {!userInfo?.hasStravaConnection && (
                        <Button variant="outline-primary" onClick={handleConnectStrava}>
                          Connect Strava
                        </Button>
                      )}
                      {userInfo?.hasStravaConnection && (
                        <Button variant="primary" onClick={handleImportActivities} disabled={importLoading}>
                          {importLoading ? 'Importing...' : 'Import Latest Activities'}
                        </Button>
                      )}
                      {!garminStatus?.connected ? (
                        <Button variant="outline-primary" onClick={handleConnectGarmin}>
                          Connect Garmin
                        </Button>
                      ) : (
                        <>
                          {garminStatus?.manualLinkRequired && (
                            <Button variant="warning" onClick={handleLinkGarminId} disabled={linkingGarminId}>
                              {linkingGarminId ? 'Linking...' : 'Link Garmin ID'}
                            </Button>
                          )}
                          <Button variant="outline-danger" onClick={handleDisconnectGarmin} disabled={disconnectingGarmin}>
                            {disconnectingGarmin ? 'Disconnecting...' : 'Disconnect Garmin'}
                          </Button>
                        </>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default AthleteScreen;
